import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {GRID_ITEM_TYPE, GridItemProps} from '@Components/base-grid/components/grid-item';
import {GridItem} from '@Components/base-grid/components/grid-item';
import {InputField} from '@Components/input-field';
import {toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {addChangeToLog} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-reducer';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import {ManagePMWStockPanelType, PMWStockItemType} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {isSafari} from 'react-device-detect';
import {useIsInViewport} from '@Hooks/useIsInViewport';
import {Text, TextSize} from '@Components/text';
import {arraysEqual} from '@Utils/array.util';
import type {GridMediaItemStorage} from '../grid-media-item';
import styles from './grid-pmwstockmedia-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

enum ActiveInfoTab {
  KEYWORDS = 'keywords',
  DESCRIPTION = 'description',
}

export interface GridPMWStockID {
  idPMmwStockMedia: string;
  idPMWStockAnimatedSticker?: string;
  idPMWStockShape?: string;
  idPMWStockStillSticker?: string;
  idPMWStockAiAnimatedSticker?: string;
  idPMWStockAiSticker?: string;
  idPMWExtractedGettySticker?: string;
  idPMWStockIcon?: string;
  idPMWStockAiIcon?: string;
  idPMWStockEmoji?: string;
  idPMWStockImage?: string;
}

export interface GridPMWStockMediaItemStorage extends GridMediaItemStorage {
  type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA;
  hashedFilename?: string;
  isHidden: number;
  keywords: string[];
  description: string;
  stockType: string;
  isEdited: boolean;
  isMask?: number;
  idPMWStock: GridPMWStockID;
  adminScore?: number;
  scrollToItem: boolean;
  iconCategory?: string;
  shapeCategory?: string;
}

interface GridPMWStockMediaItemProps extends GridItemProps {
  stockType: PMWStockItemType;
  thumbSrc: string;
  paddedThumb?: boolean;
  keywords: string[];
  isEdited: boolean;
  isDeleted?: boolean;
  scrollToItem: boolean;
  id: string;
  adminScore: number;
  iconCategory?: string;
  shapeCategory?: string;
  description: string;
}

export function GridPMWStockMediaItem({isEdited = false, isDeleted = false, style, ...props}: GridPMWStockMediaItemProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const gridReduxState = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId ?? ''];
  });
  const changesReduxState = useAppSelector((state) => {
    return state.ManagePMWStockPanel.PMWStockChanges;
  });

  const selectedGridItemIds = useAppSelector((state) => {
    return state.grids.gridSelectionGroupHashmap[props.gridId ?? ''].selectedItemIdHashmap;
  });

  const [gridItem, setGridItem] = useState<HTMLDivElement | null>(null);
  const isInViewPort = useIsInViewport(gridItem as HTMLElement);
  const [, setIsStickerBackupImageLoaded] = useState(false);
  const [, setIsShapeImageLoaded] = useState(false);

  const gridItemRef = useRef<HTMLDivElement | null>(null);

  const [focusOnKeywordContainer, setFocusOnKeywordContainer] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState<ActiveInfoTab>(ActiveInfoTab.DESCRIPTION);

  useEffect(() => {
    if (props.scrollToItem && gridItemRef.current) {
      gridItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    if (props.scrollToItem && props.gridId && !(props.id in selectedGridItemIds)) {
      dispatch(
        updateGridItem({
          gridId: props.gridId,
          itemId: props.id,
          changes: {scrollToItem: false},
        })
      );
    }
  }, [gridReduxState, selectedGridItemIds]);

  const handleOnChange = useCallback(
    (e: string, id: string): void => {
      const gridItems = gridReduxState.items as GridPMWStockMediaItemStorage[];
      const itemToChange = gridItems.find((item: GridPMWStockMediaItemStorage): boolean => {
        return item.id === id;
      });

      if (itemToChange === undefined) {
        return;
      }

      let changes: Partial<GridPMWStockMediaItemStorage>;

      if (activeInfoTab === ActiveInfoTab.DESCRIPTION) {
        const updatedDescription = e.trim();

        changes = {description: updatedDescription};
      } else {
        let updatedKeywords = e.split(',').map((keyword) => {
          return keyword.trim().toLowerCase();
        });

        if (updatedKeywords.length === 1 && updatedKeywords[0] === '') {
          updatedKeywords = [];
        } else {
          updatedKeywords = Array.from(new Set(updatedKeywords));
        }

        changes = {keywords: updatedKeywords};
      }

      let changedItem = {...itemToChange, ...changes} as Partial<GridPMWStockMediaItemStorage>;
      let edited = true;

      if (activeInfoTab === ActiveInfoTab.KEYWORDS) {
        if (arraysEqual(itemToChange.keywords, changedItem.keywords ?? [])) {
          edited = false;
        }
      } else {
        if (itemToChange.description === changedItem.description) {
          edited = false;
        }
      }

      if (changesReduxState[itemToChange.id]?.original) {
        edited = true;
        const originalItem = omit(changesReduxState[itemToChange.id].original, 'isEdited') as Partial<GridPMWStockMediaItemStorage>;
        changedItem = omit(changedItem, 'isEdited');

        if (isEqual(originalItem, changedItem)) {
          edited = false;
        }
      }

      const changedItemToLog = {id: changedItem.id, ...changes, isEdited: edited} as Partial<GridPMWStockMediaItemStorage>;
      dispatch(addChangeToLog({changeToLog: changedItemToLog, itemToChange}));
      dispatch(
        updateGridItem({
          gridId: props.gridId ?? '',
          itemId: itemToChange.id,
          changes: {...changes, isEdited: edited},
        })
      );
      dispatch(
        updateGridItem({
          gridId: ManagePMWStockPanelType.PMW_ALL_STOCK,
          itemId: itemToChange.id,
          changes: {...changes, isEdited: edited},
        })
      );
    },
    [dispatch, gridReduxState.items, changesReduxState, props.gridId, activeInfoTab]
  );

  const getInputContainer = (): ReactElement<any> => {
    const isItemSelected = props.id in selectedGridItemIds;

    return (
      <div className={`border-s-standard flex-vertical-center ${styles.keywordsContainer}`}>
        <div className={isItemSelected ? styles.itemOverlay : ''} />
        <InputField
          onSelect={(): void => {
            setFocusOnKeywordContainer(true);
          }}
          onClick={(e): void => {
            if (!isItemSelected) {
              e.stopPropagation();
            } else {
              dispatch(
                toggleGridItemSelection({
                  gridItemId: props.id,
                  gridSelectionGroupId: props.gridSelectionGroupId ?? '',
                  select: true,
                })
              );
            }
          }}
          onInputBlur={(e: string): void => {
            setFocusOnKeywordContainer(!focusOnKeywordContainer);
            return handleOnChange(e, props.id);
          }}
          multiLineRows={2}
          inputClassName={styles.textArea}
          showInputIcon={false}
          isMultiLine
          className={`_full-width body-s ${focusOnKeywordContainer ? styles.textAreaFocussed : styles.textArea}`}
          value={activeInfoTab === ActiveInfoTab.KEYWORDS ? props.keywords.join(', ') : props.description}
          maxLength={500}
        />
      </div>
    );
  };

  const getStickerThumbElement = (isInViewport: boolean): ReactElement<any> => {
    if (isSafari) {
      return (
        <img
          onLoad={(): void => {
            return setIsStickerBackupImageLoaded(true);
          }}
          className={`flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`}
          draggable="false"
          crossOrigin="anonymous"
          src={isInViewport ? props.thumbSrc : ''}
          alt={window.i18next.t('pmwjs_sticker')}
        />
      );
    }
    return (
      <img
        className={`flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`}
        draggable="false"
        onLoad={(): void => {
          return setIsStickerBackupImageLoaded(true);
        }}
        crossOrigin="anonymous"
        src={isInViewport ? props.thumbSrc : ''}
        alt={window.i18next.t('pmwjs_sticker')}
      />
    );
  };

  const getShapeThumbElement = (): ReactElement<any> => {
    return (
      <img
        onLoad={(): void => {
          return setIsShapeImageLoaded(true);
        }}
        src={props.thumbSrc}
        className={`flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`}
        alt={window.i18next.t('pmwjs_image')}
        draggable="false"
        loading="lazy"
      />
    );
  };

  const applyItemClassName = (isItemEdited: boolean, isItemDeleted: boolean): string => {
    if (isItemDeleted) {
      return styles.deletedItem;
    }
    if (isItemEdited) {
      return styles.editedItem;
    }
    return styles.ItemDefaultStyle;
  };

  return (
    <GridItem {...props} style={style} containerClassName={applyItemClassName(isEdited, isDeleted)}>
      {props.stockType === PMWStockItemType.ANIMATED_STICKER ? (
        <div
          ref={(element): void => {
            setGridItem(element);
            gridItemRef.current = element;
          }}
          className={`flex-center spacing-m-l-1 ${focusOnKeywordContainer ? styles.thumbContainerFocussed : styles.thumbContainer}`}
        >
          {React.cloneElement(getStickerThumbElement(isInViewPort))}
        </div>
      ) : (
        <div className={`flex-center spacing-m-l-1 ${focusOnKeywordContainer ? styles.thumbContainerFocussed : ''} ${styles.thumbContainer}`} ref={gridItemRef}>
          {getShapeThumbElement()}
        </div>
      )}
      <div className={styles.infoContainer}>
        {!focusOnKeywordContainer ? (
          <div className="flex-row">
            <Text val={`Score: ${props.adminScore}`} size={TextSize.XXSMALL} className={styles.adminScore} />
            <Text
              val="Keywords"
              size={TextSize.XXSMALL}
              className={`_cursor-pointer ${activeInfoTab === ActiveInfoTab.KEYWORDS ? styles.tabLinkActive : ''}`}
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                setActiveInfoTab(ActiveInfoTab.KEYWORDS);
              }}
            />
            <Text
              val="Description"
              size={TextSize.XXSMALL}
              className={`spacing-m-l-2 _cursor-pointer ${activeInfoTab === ActiveInfoTab.DESCRIPTION ? styles.tabLinkActive : ''}`}
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                setActiveInfoTab(ActiveInfoTab.DESCRIPTION);
              }}
            />
          </div>
        ) : null}

        {getInputContainer()}
      </div>
    </GridItem>
  );
}
